import type { Blocks } from '@w/content-blocks'

/** Document defaults have one spelling. Copy only changed containers. */
export function canonicalBlocks(blocks: Blocks): Blocks {
  return canonicalValue(blocks, false, false) as Blocks
}

/** `canonicalBlocks` of a command's draft (apply-intent.ts `draftBlocks`): a frozen subtree is the committed view no
 * write of this command reached, so it is passed through unvisited, as Lexical normalizes only the nodes an update
 * marked dirty. */
export function canonicalDraft(blocks: Blocks): Blocks {
  return canonicalValue(blocks, false, true) as Blocks
}

function canonicalValue(value: unknown, todo: boolean, draft: boolean): unknown {
  if (draft && Object.isFrozen(value)) return value
  if (Array.isArray(value)) {
    let next: unknown[] | undefined
    for (let index = 0; index < value.length; index += 1) {
      const child: unknown = value[index]
      const canonical = canonicalValue(child, todo, draft)
      if (canonical === child) continue
      next ??= value.slice()
      next[index] = canonical
    }
    return next ?? value
  }
  if (!value || typeof value !== 'object') return value
  const record = value as Record<string, unknown>
  if (record.type === 'text' && typeof record.text === 'string') {
    return canonicalValue({ ...record, type: 'chars', text: [{ id: '', text: record.text }] }, todo, draft)
  }
  let next: Record<string, unknown> | undefined
  for (const [key, child] of Object.entries(record)) {
    if (key.startsWith('$') || child === undefined ||
      (record.type === 'td' && (key === 'rowSpan' || key === 'colSpan') && child === 1) ||
      (key === 'format' && record.type === 'chars' && child === 0) ||
      (key === 'spread' && (record.type === 'li' ||
        // A single-item spread list round-trips as `:::list{spread="true"}` (content-blocks serializer.ts).
        (record.type === 'list' && child !== true))) ||
      (key === 'done' && record.type === 'li' && !todo)) {
      next ??= { ...record }
      delete next[key]
      continue
    }
    const canonical = canonicalValue(child,
      key === 'children' && record.type === 'list' && record.subtype === 'todo', draft)
    if (canonical === child) continue
    next ??= { ...record }
    next[key] = canonical
  }
  return next ?? value
}
